import get from 'lodash.get';
import sumBy from 'lodash.sumby';
import omit from 'lodash.omit';
import orderBy from 'lodash.orderby';
import mapValues from 'lodash.mapvalues';

// Global Types
import { AppState } from 'services/types';

// Global Selectors
import { makeSelectError } from 'services/error/selectors';
import { selectHarvestSchemaId } from 'services/schemas/selectors';
// @ts-ignore
import { selectGroups } from 'services/groups/selectors';

// Global Utils
import createDeepEqualSelector from 'utils/reselect/typescript/createDeepEqualSelector';

// Local Types
import {
    HarvestEventGroupId,
    QuantitySchedule,
    HarvestEventsState,
    HarvestEventsProps,
    Job
} from './types';

const colorGrades: Record<string, string> = {
    HIGH: 'High',
    STANDARD: 'Standard'
};

const selectHarvestEventState = (
    state: Record<string, any>
): HarvestEventsState => state.harvestEvents;

const selectHarvestEventUuid = (
    state: AppState,
    props: HarvestEventsProps
): string | undefined => props.harvestEventUuid;

const selectGroupId = (
    state: AppState,
    props: HarvestEventsProps
): number | null | undefined => props.groupId;

const selectHarvestEventUpdates = createDeepEqualSelector(
    [selectHarvestEventState],
    (harvestEventsStateState) => harvestEventsStateState.updates
);

const selectHarvestEventErrors = createDeepEqualSelector(
    [selectHarvestEventState],
    (harvestEventsStateState) => harvestEventsStateState.errors
);

const selectHarvestEventErrorsKeyedByUuid = createDeepEqualSelector(
    [
        selectHarvestEventErrors,
        selectHarvestEventUpdates,
        makeSelectError('HARVEST_EVENTS_SAVE')
    ],
    (harvestEventErrorsState, harvestEventUpdatesState, errorState) => {
        const harvestEventUpdatesUuids = Object.keys(harvestEventUpdatesState);

        if (harvestEventErrorsState.length) {
            return harvestEventErrorsState.reduce<Record<string, string>>(
                (accumulator, currentValue) => {
                    const { message, uuids } = currentValue;
                    uuids.forEach((uuid) => {
                        accumulator[uuid] = message;
                    });
                    return accumulator;
                },
                {}
            );
        }

        if (errorState) {
            const errorsKeyedByUuid: Record<string, string> = {};
            harvestEventUpdatesUuids.forEach((uuid: string) => {
                errorsKeyedByUuid[uuid] = errorState;
            });
            return errorsKeyedByUuid;
        }

        return {};
    }
);

const selectHarvestEventErrorsExist = createDeepEqualSelector(
    [selectHarvestEventErrorsKeyedByUuid],
    (harvestEventErrorsKeyedByUuidState) =>
        !!Object.keys(harvestEventErrorsKeyedByUuidState).length
);

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectHarvestEventErrorMessage = () =>
    createDeepEqualSelector(
        [selectHarvestEventErrorsKeyedByUuid, selectHarvestEventUuid],
        (harvestEventErrorsKeyedByUuidState, harvestEventUuidProp) => {
            if (!harvestEventUuidProp) {
                return undefined;
            }
            return get(
                harvestEventErrorsKeyedByUuidState,
                harvestEventUuidProp
            );
        }
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectHarvestEventErrorMessageExists = () =>
    createDeepEqualSelector(
        [makeSelectHarvestEventErrorMessage()],
        (harvestEventErrorMessage) => !!harvestEventErrorMessage
    );

const selectHarvestEvents = createDeepEqualSelector(
    [selectHarvestEventState],
    (harvestEventsStateState) => {
        const { data, updates, deletes } = harvestEventsStateState;

        const harvestEvents = {
            ...data,
            ...updates
        };

        const harvestEventsWithDeletesApplied = omit(harvestEvents, deletes);

        return mapValues(harvestEventsWithDeletesApplied, (harvestEvent) => {
            const { quantitySchedules } = harvestEvent;

            return {
                ...harvestEvent,
                scheduledQuantity: sumBy(quantitySchedules, 'value')
            };
        });
    }
);

const selectHarvestEventsArray = createDeepEqualSelector(
    selectHarvestEvents,
    (harvestEventsState) => Object.values(harvestEventsState)
);

const selectUnsavedHarvestEvents = createDeepEqualSelector(
    [selectHarvestEventState, selectHarvestSchemaId],
    (harvestEventsStateState, harvestSchemaIdState) => {
        const { data, updates, deletes } = harvestEventsStateState;

        const updatesArray = Object.values(updates);

        const formattedUpdates: any = updatesArray.map((value) => {
            const { uuid } = value;
            const alreadyExists = !!data[uuid];
            const emptyValue = alreadyExists ? null : undefined;

            return {
                ...(!alreadyExists
                    ? {
                          schemaId: harvestSchemaIdState
                      }
                    : {}),
                uuid,
                entityId: value.entityId,
                groupIds: value.groupIds || [],
                orderedAt: value.orderedAt,
                isDifferentFromForecast: value.isDifferentFromForecast,
                jobs: value.jobs.map((job: Job) => {
                    return {
                        jobType: job.jobType,
                        assignedUsers: job.assignedUsers.map(
                            (assignedUser) => assignedUser.email
                        )
                    };
                }),
                ...(value.harvestEvent
                    ? {
                          harvestEvent: {
                              colorGrade:
                                  value.harvestEvent.colorGrade || emptyValue,
                              harvestBinSuitabilityId:
                                  value.harvestEvent.harvestBinSuitabilityId,
                              harvestPalletTypeId:
                                  value.harvestEvent.harvestPalletTypeId,
                              harvestStorageTypeId:
                                  value.harvestEvent.harvestStorageTypeId,
                              harvestSubmissionProfileId:
                                  value.harvestEvent.harvestSubmissionProfileId,
                              harvestGrowingMethodId:
                                  value.harvestEvent.harvestGrowingMethodId,
                              harvestLocationId:
                                  value.harvestEvent.harvestLocationId,
                              ...(!alreadyExists
                                  ? {
                                        pickTypeId:
                                            value.harvestEvent.pickTypeId
                                    }
                                  : {})
                          }
                      }
                    : {}),
                ...(value.quantitySchedules
                    ? {
                          quantitySchedules: value.quantitySchedules.map(
                              (quantitySchedule) => ({
                                  date: quantitySchedule.date,
                                  value: quantitySchedule.value
                              })
                          )
                      }
                    : {})
            };
        });

        const create = formattedUpdates.filter((value: any) => value.schemaId);
        const update = formattedUpdates.filter((value: any) => !value.schemaId);

        const hasCreates = !!create.length;
        const hasUpdates = !!update.length;
        const hasDeletes = !!deletes.length;

        if (!hasCreates && !hasUpdates && !hasDeletes) {
            return undefined;
        }

        return {
            ...(hasCreates
                ? {
                      create
                  }
                : {}),
            ...(hasUpdates
                ? {
                      update
                  }
                : {}),
            ...(hasDeletes
                ? {
                      delete: deletes
                  }
                : {})
        };
    }
);

const selectHarvestEvent = createDeepEqualSelector(
    [selectHarvestEvents, selectHarvestEventUuid],
    (harvestEventsState, harvestEventUuidProp) => {
        if (!harvestEventUuidProp) {
            return undefined;
        }
        return get(harvestEventsState, harvestEventUuidProp);
    }
);

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectHarvestEventHasUnsavedChanges = () =>
    createDeepEqualSelector(
        [selectHarvestEventUpdates, selectHarvestEventUuid],
        (harvestEventsUpdatesState, harvestEventUuidProp) => {
            if (!harvestEventUuidProp) {
                return false;
            }
            return !!get(harvestEventsUpdatesState, harvestEventUuidProp);
        }
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectHarvestEvent = () => selectHarvestEvent;

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectHarvestEventEntityId = () =>
    createDeepEqualSelector(selectHarvestEvent, (harvestEventState) =>
        get(harvestEventState, 'entityId')
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectHarvestEventEntityName = () =>
    createDeepEqualSelector(selectHarvestEvent, (harvestEventState) =>
        get(harvestEventState, 'entityName')
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectHarvestEventGroupId = () =>
    createDeepEqualSelector(selectHarvestEvent, (harvestEventState) =>
        get(harvestEventState, 'groupId')
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectHarvestEventGroupIds = () =>
    createDeepEqualSelector(
        [selectHarvestEvent, selectGroups],
        (harvestEventState, groupsState) => {
            const groupIds: HarvestEventGroupId[] =
                get(harvestEventState, 'groupIds') || [];
            // Check groups exist in state to filier out any deleted ones
            const groups = groupIds
                .map((groupId) => get(groupsState, groupId))
                .filter((groupName) => groupName);
            const groupsOrderedByName = orderBy(groups, 'name');
            return groupsOrderedByName.map((group) => group.id);
        }
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectHarvestEventHasMultipleGroups = () =>
    createDeepEqualSelector(
        selectHarvestEvent,
        (harvestEventState) =>
            get(harvestEventState, ['groupIds', 'length'], 0) > 1
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectHarvestEventPickTypeName = () =>
    createDeepEqualSelector(selectHarvestEvent, (harvestEventState) =>
        get(harvestEventState, 'pickTypeName')
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectHarvestEventPickTypeExternalId = () =>
    createDeepEqualSelector(selectHarvestEvent, (harvestEventState) => {
        const pickTypeName = get(harvestEventState, 'pickTypeName');
        if (!pickTypeName) return null;
        return pickTypeName.replace('Pick', '').trim().charAt(0);
    });

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectHarvestEventHarvestPickTypeId = () =>
    createDeepEqualSelector(selectHarvestEvent, (harvestEventState) =>
        get(harvestEventState, 'harvestEvent.pickTypeId')
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectHarvestEventHarvestBinSuitabilityId = () =>
    createDeepEqualSelector(selectHarvestEvent, (harvestEventState) =>
        get(harvestEventState, 'harvestEvent.harvestBinSuitabilityId')
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectHarvestEventHarvestGrowingMethodId = () =>
    createDeepEqualSelector(selectHarvestEvent, (harvestEventState) =>
        get(harvestEventState, 'harvestEvent.harvestGrowingMethodId')
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectHarvestEventHarvestLocationId = () =>
    createDeepEqualSelector(selectHarvestEvent, (harvestEventState) =>
        get(harvestEventState, 'harvestEvent.harvestLocationId')
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectHarvestEventHarvestPalletTypeId = () =>
    createDeepEqualSelector(selectHarvestEvent, (harvestEventState) =>
        get(harvestEventState, 'harvestEvent.harvestPalletTypeId')
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectHarvestEventHarvestStorageTypeId = () =>
    createDeepEqualSelector(selectHarvestEvent, (harvestEventState) =>
        get(harvestEventState, 'harvestEvent.harvestStorageTypeId')
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectHarvestEventHarvestSubmissionProfileId = () =>
    createDeepEqualSelector(selectHarvestEvent, (harvestEventState) =>
        get(harvestEventState, 'harvestEvent.harvestSubmissionProfileId')
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectHarvestEventId = () =>
    createDeepEqualSelector(selectHarvestEvent, (harvestEventState) =>
        get(harvestEventState, 'id')
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectHarvestEventNumberOfContainers = () =>
    createDeepEqualSelector(selectHarvestEvent, (harvestEventState) =>
        get(harvestEventState, 'harvestEventStats.numberOfContainers')
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectHarvestEventOverallDefectPercentage = () =>
    createDeepEqualSelector(selectHarvestEvent, (harvestEventState) =>
        get(harvestEventState, 'harvestEventStats.overallDefectPercentage')
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectHarvestEventVarietyName = () =>
    createDeepEqualSelector(selectHarvestEvent, (harvestEventState) =>
        get(harvestEventState, 'varietyName')
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectHarvestEventFirstJobAssignedUsers = () =>
    createDeepEqualSelector(selectHarvestEvent, (harvestEventState) =>
        get(harvestEventState, ['jobs', 0, 'assignedUsers'])
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectHarvestEventFirstJobJobState = () =>
    createDeepEqualSelector(selectHarvestEvent, (harvestEventState) =>
        get(harvestEventState, ['jobs', 0, 'jobState'])
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectHarvestEventSecondJobAssignedUsers = () =>
    createDeepEqualSelector(selectHarvestEvent, (harvestEventState) =>
        get(harvestEventState, ['jobs', 1, 'assignedUsers'])
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectHarvestEventSecondJobJobState = () =>
    createDeepEqualSelector(selectHarvestEvent, (harvestEventState) =>
        get(harvestEventState, ['jobs', 1, 'jobState'])
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectHarvestEventColorGrade = () =>
    createDeepEqualSelector(selectHarvestEvent, (harvestEventState) =>
        get(harvestEventState, 'harvestEvent.colorGrade')
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectHarvestEventColorGradeText = () =>
    createDeepEqualSelector(
        makeSelectHarvestEventColorGrade(),
        (colorGrade) => {
            if (colorGrade) {
                return colorGrades[colorGrade];
            }

            return '';
        }
    );

const selectHarvestQuantitySchedules = createDeepEqualSelector(
    selectHarvestEvent,
    (harvestEventState) => {
        return get(harvestEventState, ['quantitySchedules']);
    }
);

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectHarvestQuantitySchedules = () => selectHarvestQuantitySchedules;

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectHarvestEventStartDate = () =>
    createDeepEqualSelector(
        selectHarvestQuantitySchedules,
        (harvestQuantitySchedules) => get(harvestQuantitySchedules, [0, 'date'])
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectHarvestQuantitySchedulesCount = () =>
    createDeepEqualSelector(
        selectHarvestQuantitySchedules,
        (harvestQuantitySchedules) => get(harvestQuantitySchedules, 'length')
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectHarvestEventEndDate = () =>
    createDeepEqualSelector(
        selectHarvestQuantitySchedules,
        (harvestQuantitySchedules) => {
            if (!harvestQuantitySchedules || !harvestQuantitySchedules.length) {
                return undefined;
            }
            return get(harvestQuantitySchedules, [
                harvestQuantitySchedules.length - 1,
                'date'
            ]);
        }
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectHarvestEventQuantitySchedulesSummary = () =>
    createDeepEqualSelector(
        [selectHarvestEvent, selectGroupId],
        (harvestEventState, groupIdProp) => {
            if (!harvestEventState) {
                return null;
            }
            const { quantitySchedules, forecastedQuantity } = harvestEventState;
            const numberOfGroups = get(
                harvestEventState,
                ['groupIds', 'length'],
                0
            );

            if (!quantitySchedules || !quantitySchedules.length) {
                return `D1-${forecastedQuantity || 0}`;
            }
            return quantitySchedules
                .map((quantitySchedule: QuantitySchedule, index: number) => {
                    const quantityScheduleValue = get(
                        quantitySchedule,
                        'value',
                        0
                    );
                    // If groupId prop exists then divide the value by the number of groups
                    const splitQuantityScheduleValue = groupIdProp
                        ? quantityScheduleValue / numberOfGroups
                        : quantityScheduleValue;

                    // Round to 1 decimal place
                    const roundedSplitQuantityScheduleValue =
                        Math.round(splitQuantityScheduleValue * 10) / 10;
                    return `D${
                        index + 1
                    }-${roundedSplitQuantityScheduleValue.toLocaleString()}`;
                })
                .join(' / ');
        }
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectHarvestEventScheduledQuantity = () =>
    createDeepEqualSelector(
        selectHarvestQuantitySchedules,
        (harvestQuantitySchedules) => {
            if (!harvestQuantitySchedules || !harvestQuantitySchedules.length) {
                return 0;
            }
            return sumBy(harvestQuantitySchedules, 'value');
        }
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectHarvestEventForecastedQuantity = () =>
    createDeepEqualSelector(selectHarvestEvent, (harvestEventState) =>
        get(harvestEventState, 'forecastedQuantity')
    );

export {
    selectHarvestEventUuid,
    selectHarvestEventErrors,
    selectHarvestEventErrorsExist,
    makeSelectHarvestEventErrorMessage,
    makeSelectHarvestEventErrorMessageExists,
    selectHarvestEvents,
    selectHarvestEventsArray,
    selectUnsavedHarvestEvents,
    makeSelectHarvestEventHasUnsavedChanges,
    makeSelectHarvestEvent,
    makeSelectHarvestEventEntityId,
    makeSelectHarvestEventEntityName,
    makeSelectHarvestEventGroupId,
    makeSelectHarvestEventGroupIds,
    makeSelectHarvestEventHasMultipleGroups,
    makeSelectHarvestEventPickTypeName,
    makeSelectHarvestEventPickTypeExternalId,
    makeSelectHarvestEventHarvestPickTypeId,
    makeSelectHarvestEventHarvestBinSuitabilityId,
    makeSelectHarvestEventHarvestGrowingMethodId,
    makeSelectHarvestEventHarvestLocationId,
    makeSelectHarvestEventHarvestPalletTypeId,
    makeSelectHarvestEventHarvestStorageTypeId,
    makeSelectHarvestEventHarvestSubmissionProfileId,
    makeSelectHarvestEventId,
    makeSelectHarvestEventNumberOfContainers,
    makeSelectHarvestEventOverallDefectPercentage,
    makeSelectHarvestEventVarietyName,
    makeSelectHarvestEventFirstJobAssignedUsers,
    makeSelectHarvestEventFirstJobJobState,
    makeSelectHarvestEventSecondJobAssignedUsers,
    makeSelectHarvestEventSecondJobJobState,
    makeSelectHarvestEventStartDate,
    makeSelectHarvestEventEndDate,
    makeSelectHarvestQuantitySchedules,
    makeSelectHarvestQuantitySchedulesCount,
    makeSelectHarvestEventQuantitySchedulesSummary,
    makeSelectHarvestEventScheduledQuantity,
    makeSelectHarvestEventForecastedQuantity,
    makeSelectHarvestEventColorGrade,
    makeSelectHarvestEventColorGradeText
};
